import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import {
  ensureIntakeMedicalInfo,
  getIntakeMedicalSelectionArray,
  setIntakeMedicalSelectionArray,
} from './utils';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IntakeQuestionnaireState } from '../../types';
import type {
  CreateIntakeMedicalSelectionPayload,
  DeleteIntakeMedicalDiagnosisPayload,
  DeleteIntakeMedicalSelectionPayload,
  IIntakeMedicalDiagnosis,
  IIntakeMedicalInfo,
  IIntakeMedicalSelection,
  IntakeMedicalThunks,
} from './types';

export function addIntakeMedicalCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicalThunks,
) {
  addUpdateIntakeMedicalInfoCases(builder, thunks);
  addCreateIntakeMedicalDiagnosisCases(builder, thunks);
  addUpdateIntakeMedicalDiagnosisCases(builder, thunks);
  addDeleteIntakeMedicalDiagnosisCases(builder, thunks);
  addCreateIntakeMedicalSelectionCases(builder, thunks);
  addDeleteIntakeMedicalSelectionCases(builder, thunks);
}

function addUpdateIntakeMedicalInfoCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicalThunks,
) {
  const { updateIntakeMedicalInfo } = thunks;

  builder
    .addCase(updateIntakeMedicalInfo.pending, (state) => {
      state.updateMedicalInfoLoading = true;
      state.error = null;
    })
    .addCase(
      updateIntakeMedicalInfo.fulfilled,
      (state, action: PayloadAction<IIntakeMedicalInfo>) => {
        state.updateMedicalInfoLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        // The scalar PATCH response snapshots the diagnosis/selection lists as of when
        // the backend handled it, which can predate list changes saved while it was in
        // flight — adopting them wholesale rolls those back (e.g. a just-added diagnosis
        // loses fields or disappears). The lists are maintained by their own reducers,
        // so keep the store's copies and take only the scalars from the response.
        const currentMedicalInfo = state.intakeQuestionnaireDetails.medical_info;
        state.intakeQuestionnaireDetails.medical_info = currentMedicalInfo
          ? {
              ...action.payload,
              diagnoses: currentMedicalInfo.diagnoses,
              early_interventions: currentMedicalInfo.early_interventions,
              pregnancy_issues: currentMedicalInfo.pregnancy_issues,
              regressions: currentMedicalInfo.regressions,
            }
          : action.payload;
      },
    )
    .addCase(updateIntakeMedicalInfo.rejected, (state, action) => {
      state.updateMedicalInfoLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateIntakeMedicalDiagnosisCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicalThunks,
) {
  const { createIntakeMedicalDiagnosis } = thunks;

  builder
    .addCase(createIntakeMedicalDiagnosis.pending, (state) => {
      state.createMedicalDiagnosisLoading = true;
      state.error = null;
    })
    .addCase(
      createIntakeMedicalDiagnosis.fulfilled,
      (state, action: PayloadAction<IIntakeMedicalDiagnosis>) => {
        state.createMedicalDiagnosisLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        const medicalInfo = ensureIntakeMedicalInfo(state.intakeQuestionnaireDetails);
        medicalInfo.diagnoses = [...medicalInfo.diagnoses, action.payload];
      },
    )
    .addCase(createIntakeMedicalDiagnosis.rejected, (state, action) => {
      state.createMedicalDiagnosisLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateIntakeMedicalDiagnosisCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicalThunks,
) {
  const { updateIntakeMedicalDiagnosis } = thunks;

  builder
    .addCase(updateIntakeMedicalDiagnosis.pending, (state) => {
      state.updateMedicalDiagnosisLoading = true;
      state.error = null;
    })
    .addCase(
      updateIntakeMedicalDiagnosis.fulfilled,
      (state, action: PayloadAction<IIntakeMedicalDiagnosis>) => {
        state.updateMedicalDiagnosisLoading = false;
        if (!state.intakeQuestionnaireDetails?.medical_info) return;
        const list = state.intakeQuestionnaireDetails.medical_info.diagnoses;
        const index = list.findIndex((row) => row.id === action.payload.id);
        if (index !== -1) list[index] = action.payload;
      },
    )
    .addCase(updateIntakeMedicalDiagnosis.rejected, (state, action) => {
      state.updateMedicalDiagnosisLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeMedicalDiagnosisCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicalThunks,
) {
  const { deleteIntakeMedicalDiagnosis } = thunks;

  builder
    .addCase(deleteIntakeMedicalDiagnosis.pending, (state) => {
      state.deleteMedicalDiagnosisLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeMedicalDiagnosis.fulfilled,
      (
        state,
        action: PayloadAction<void, string, { arg: DeleteIntakeMedicalDiagnosisPayload }>,
      ) => {
        state.deleteMedicalDiagnosisLoading = false;
        if (!state.intakeQuestionnaireDetails?.medical_info) return;
        state.intakeQuestionnaireDetails.medical_info.diagnoses =
          state.intakeQuestionnaireDetails.medical_info.diagnoses.filter(
            (row) => row.id !== action.meta.arg.diagnosisId,
          );
      },
    )
    .addCase(deleteIntakeMedicalDiagnosis.rejected, (state, action) => {
      state.deleteMedicalDiagnosisLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateIntakeMedicalSelectionCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicalThunks,
) {
  const { createIntakeMedicalSelection } = thunks;

  builder
    .addCase(createIntakeMedicalSelection.pending, (state) => {
      state.createMedicalSelectionLoading = true;
      state.error = null;
    })
    .addCase(
      createIntakeMedicalSelection.fulfilled,
      (
        state,
        action: PayloadAction<
          IIntakeMedicalSelection,
          string,
          { arg: CreateIntakeMedicalSelectionPayload }
        >,
      ) => {
        state.createMedicalSelectionLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        const medicalInfo = ensureIntakeMedicalInfo(state.intakeQuestionnaireDetails);
        const { selection_type, value } = action.meta.arg.data;
        // Assign a new array so subscribers see a new list reference. Take type/value
        // from the request — the create response may omit them (or use a different
        // shape), and the list UI keys off those fields.
        setIntakeMedicalSelectionArray(medicalInfo, selection_type, [
          ...getIntakeMedicalSelectionArray(medicalInfo, selection_type),
          { ...action.payload, selection_type, value },
        ]);
      },
    )
    .addCase(createIntakeMedicalSelection.rejected, (state, action) => {
      state.createMedicalSelectionLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeMedicalSelectionCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeMedicalThunks,
) {
  const { deleteIntakeMedicalSelection } = thunks;

  builder
    .addCase(deleteIntakeMedicalSelection.pending, (state) => {
      state.deleteMedicalSelectionLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeMedicalSelection.fulfilled,
      (
        state,
        action: PayloadAction<void, string, { arg: DeleteIntakeMedicalSelectionPayload }>,
      ) => {
        state.deleteMedicalSelectionLoading = false;
        const medicalInfo = state.intakeQuestionnaireDetails?.medical_info;
        if (!medicalInfo) return;
        const { selectionType, selectionId } = action.meta.arg;
        setIntakeMedicalSelectionArray(
          medicalInfo,
          selectionType,
          getIntakeMedicalSelectionArray(medicalInfo, selectionType).filter(
            (row) => row.id !== selectionId,
          ),
        );
      },
    )
    .addCase(deleteIntakeMedicalSelection.rejected, (state, action) => {
      state.deleteMedicalSelectionLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}
